import { Link } from "@tanstack/react-router"
import { Input } from "design-system/atoms/Input"
import { Textarea } from "design-system/atoms/Textarea"
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "design-system/organisms/Form"
import MessengerPageFormSelector from "modules/campaigns/components/Sections/triggers/messenger/MessengerPageFormSelector"
import { UpdateCampaignSchemaFrontend } from "modules/campaigns/hooks/useCampaignForm"
import { useFormContext } from "react-hook-form"
import { Trans, useTranslation } from "react-i18next"
import { z } from "zod"

type Props = {
  isDisabled: boolean
}

type FormValues = z.infer<typeof UpdateCampaignSchemaFrontend>
type MessengerDm = Extract<
  NonNullable<FormValues["triggerIntegration"]>,
  { type: "messengerDirectMessage" }
>

export function MessengerDirectMessageForm({ isDisabled }: Props) {
  const { t } = useTranslation()
  const form = useFormContext<FormValues>()
  const triggerIntegration = form.watch("triggerIntegration")
  const messengerDm =
    triggerIntegration?.type === "messengerDirectMessage" ? triggerIntegration : undefined

  // Rebuild the full Messenger DM variant on every edit so `type` and the account id survive partial changes.
  const buildMessengerDm = (patch: Partial<MessengerDm>): MessengerDm => ({
    type: "messengerDirectMessage",
    messengerIntegrationId: messengerDm?.messengerIntegrationId ?? "",
    delayInDays: messengerDm?.delayInDays ?? undefined,
    discardPhrases: messengerDm?.discardPhrases ?? [],
    ...patch,
  })

  return (
    <>
      <FormField
        control={form.control}
        name="triggerIntegration"
        render={({ field }) => (
          <div>
            <FormItem className="flex flex-col gap-2">
              <FormLabel>
                {t("campaigns.trigger.metadata.messengerDirectMessage.selectAccount")}
              </FormLabel>
              <FormControl>
                <MessengerPageFormSelector
                  value={messengerDm?.messengerIntegrationId}
                  emptyPlaceholder={t(
                    "campaigns.trigger.metadata.messengerDirectMessage.emptyPlaceholder",
                  )}
                  placeholder={t(
                    "campaigns.trigger.metadata.messengerDirectMessage.emptyPlaceholder",
                  )}
                  searchPlaceholder={t(
                    "campaigns.trigger.metadata.messengerDirectMessage.searchPlaceholder",
                  )}
                  onChange={(account) => {
                    field.onChange(
                      account ? buildMessengerDm({ messengerIntegrationId: account.value }) : null,
                      { shouldDirty: true },
                    )
                  }}
                  hasCreateNew
                  isDisabled={isDisabled}
                />
              </FormControl>
              <FormMessage />
              <span className="text-sm text-muted-foreground">
                <Trans
                  i18nKey={"campaigns.trigger.metadata.messengerDirectMessage.accountDescription"}
                  components={{
                    a: (
                      <Link
                        className="underline text-muted-foreground"
                        to="/settings/channels/messenger"
                        target="_blank"
                      />
                    ),
                  }}
                />
              </span>
            </FormItem>
          </div>
        )}
      />
      <FormField
        control={form.control}
        name="triggerIntegration"
        render={({ field }) => (
          <FormItem className="flex flex-col gap-2">
            <FormLabel>
              {t("campaigns.trigger.metadata.messengerDirectMessage.discardPhrases.title")}
            </FormLabel>
            <FormControl>
              <Textarea
                value={messengerDm?.discardPhrases?.join("\n") ?? ""}
                placeholder={t(
                  "campaigns.trigger.metadata.messengerDirectMessage.discardPhrases.placeholder",
                )}
                disabled={isDisabled}
                onChange={(event) =>
                  field.onChange(
                    buildMessengerDm({
                      discardPhrases: event.target.value.split("\n"),
                    }),
                    { shouldDirty: true },
                  )
                }
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="triggerIntegration"
        render={({ field }) => (
          <div>
            <FormItem className="flex flex-col gap-1">
              <FormLabel>
                {t(
                  "campaigns.trigger.metadata.messengerDirectMessage.disableAfterHumanInteraction.title",
                )}
              </FormLabel>
              <FormControl>
                <div className="flex items-start gap-3">
                  <span className="text-muted-foreground text-xs block flex-1">
                    {t(
                      "campaigns.trigger.metadata.messengerDirectMessage.disableAfterHumanInteraction.description",
                    )}
                  </span>
                  <Input
                    wrapperClassName="w-auto"
                    className="w-24 shrink-0"
                    disabled={isDisabled}
                    value={messengerDm?.delayInDays ?? ""}
                    min={1}
                    type="number"
                    placeholder={t(
                      "campaigns.trigger.metadata.messengerDirectMessage.disableAfterHumanInteraction.placeholder",
                    )}
                    onChange={(value) => {
                      const delayInDays = value.target.value
                        ? Number(value.target.value)
                        : undefined

                      field.onChange(buildMessengerDm({ delayInDays }), { shouldDirty: true })
                    }}
                  />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          </div>
        )}
      />
    </>
  )
}
